import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames/bind';
import useWindowSize from '@rooks/use-window-size';
import { useCountUp } from 'react-countup';

import { RightShard } from '@components/shards';
import SnappingSlider from '@components/snapping-slider';
import { Quote } from '@components/quote-slide/quote';
import withSnapPoint from '@components/snapping-slider/visibility-monitor';
import RichText from '@components/rich-text';
import { WagtailImage } from '@components/image';
import IconButton from '@components/icon-button';
import LinkIcon from '@icons/link';
import renderValue, { getMaxValue } from '@utils/number';
import { forTabletLandscape } from '@utils/responsive';
import Lightbox from '@components/story-slide/lightbox';
import css from './story.module.scss';

const styles = classnames.bind(css);

const StorySlide = (props) => {
    const { index, active, sections, currentSlide, parentRef, slideRef, extraImages, setIsolated } = props;
    const [modalOpen, setModalOpen] = useState(false);
    const [partialScroll, setPartialScroll] = useState({});
    const { innerWidth } = useWindowSize();
    const [isMobile, setIsMobile] = useState(false);

    const renderSlide = (section) => {
        switch (section.type) {
            case 'text':
                return function StoryContainer(props) {
                    return <StoryTextSlide {...section} {...props} />;
                };
            case 'blockquote':
                return function StoryContainer(props) {
                    return <StoryQuoteSlide {...section} {...props} />;
                };
            case 'stat-label':
                return function StoryContainer(props) {
                    return <StoryStatSlide {...section} {...props} />;
                };
        }
    };

    // Combine story section images with extra gallery images.
    const images = sections
        .map((section) =>
            !!section.galleryImage
                ? {
                      source: section.galleryImage,
                      caption: section.label,
                  }
                : null,
        )
        .concat(
            extraImages.map((image) =>
                !!image.image
                    ? {
                          source: image.image.galleryImage.src,
                          caption: image.caption,
                      }
                    : null,
            ),
        );

    // Filter out duplicates and null values.
    const galleryImages = images.filter((currentImage, index) => {
        if (currentImage == null) return false;

        let imageIsDuplicate = false;
        images.slice(0, index + 1).map((prevImage, nestedIndex) => {
            if (prevImage == null) return false;
            if (prevImage.source == currentImage.source && index != nestedIndex) {
                imageIsDuplicate = prevImage.source;
            }
        });

        return !imageIsDuplicate;
    });

    // Should only show gallery if we have multiple images to show
    const shouldRenderGallery = galleryImages.length > 1;

    const toggleGallery = () => {
        setIsolated(!modalOpen);
        setModalOpen(!modalOpen);
    };

    const LightboxButton = ({ id, hidden }) => (
        // Note: this previously tried to use a camera icon and was throwing an error (it passed the string "camera") to the IconButton)
        // As this functionality does not work anyway currently, and the button is hidden, just use the link icon for now
        <IconButton
            id={id}
            className={styles('story-slide__lightbox-button')}
            onClick={toggleGallery}
            icon={LinkIcon}
            iconAlign="left"
            label="View more images"
            aria-hidden={hidden}
            aria-label="Click button to show image gallery"
        />
    );

    const LightboxContainer = () => (
        <>
            <Lightbox images={galleryImages} open={modalOpen} onClose={toggleGallery} />
        </>
    );

    const nestedSlidesActive = currentSlide > index - 1 && currentSlide < index + sections.length;
    const ImageContents = ({ mobile, children, ...props }) => (
        <div aria-hidden className={styles('story-slide__image')}>
            <div className={styles('story-slide__image__shaddow')} />
            {sections.map((section, nestedIndex) => {
                const image = section.image || sections[0].image;
                if (image) {
                    return (
                        <WagtailImage
                            key={nestedIndex}
                            renditionName="halfCover"
                            image={section.image || sections[0].image}
                            className={styles('story-slide__image__inner')}
                            style={{
                                zIndex: mobile ? 1 : -200 + index,
                                opacity:
                                    index + nestedIndex == currentSlide ||
                                    index + nestedIndex == currentSlide - 1 ||
                                    nestedIndex == 0
                                        ? 1
                                        : 0,
                            }}
                        />
                    );
                }
            })}
            {children}
        </div>
    );

    // Mobile: Create one snapable slide!
    useEffect(() => {
        setIsMobile(!forTabletLandscape(innerWidth));
    }, []);
    if (isMobile) {
        const MobileSlide = withSnapPoint((props) => (
            <div ref={props.slideRef}>
                <div className={styles('story-slide__mobile-header')}>
                    <ImageContents mobile />
                </div>
                {sections.map(renderSlide).map((Component) => (
                    <Component active={currentSlide == index} />
                ))}
            </div>
        ));
        return <MobileSlide {...props} />;
    }

    // Desktop: create multiple snapable slides.
    return (
        <>
            {/* <RightShard style={{ zIndex: 0 }} /> */}
            <ImageContents>
                <LightboxContainer />
            </ImageContents>
            {sections.map(renderSlide).map((Component, nestedIndex) => {
                const NestedStorySlide = withSnapPoint(Component);
                return (
                    <>
                        <NestedStorySlide
                            {...props}
                            index={index + nestedIndex}
                            key={`nested-slide-${index}.${nestedIndex}`}
                            Component={Component}
                            // data-focusable-elements={JSON.stringify([`lightbox-button-${nestedIndex}`])}
                        />
                        {shouldRenderGallery && <LightboxButton />}
                    </>
                );
            })}
        </>
    );
};

const StoryTextSlide = ({ slideRef, title, content, active, ...props }) => {
    return (
        <div ref={slideRef} className={styles('story-text-slide')}>
            <RichText tag="span" className={styles('story-text-slide__title')} aria-label={title}>
                {title}
            </RichText>
            <RichText className={styles('story-text-slide__content')}>{content}</RichText>
        </div>
    );
};

const StoryQuoteSlide = ({ slideRef, quote, cite, ...props }) => {
    return (
        <div ref={slideRef} className={styles('story-quote-slide')}>
            <Quote quote={quote} cite={cite} className={styles('story-quote-slide__block')} />
        </div>
    );
};

const StoryStatSlide = ({ slideRef, active, stat, label, scrollerRef, ...props }) => {
    const [started, setStarted] = useState(false);
    const { countUp, update } = useCountUp({
        end: 0,
        duration: 1,
    });

    useEffect(() => {
        if (active) {
            update(getMaxValue(stat.value));
        }
    }, [active]);

    return (
        <div ref={slideRef} className={styles('story-stat-slide')}>
            <div>
                <span className={styles('story-stat-slide__stat')}>{renderValue(stat.value, countUp)}</span>
                <RichText className={styles('story-stat-slide__label')}>{label}</RichText>
            </div>
        </div>
    );
};

export default StorySlide;
